"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import Link from "next/link";
import Navbar from "@/app/components/Navbar";
import Footer from "@/app/components/Footer";
import { Turnstile } from "@marsidev/react-turnstile";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!turnstileToken) {
      setError("Please complete the security verification.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/contact/submit`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...form,
          turnstileToken,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
      } else {
        setError(data.error || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Failed to connect to the server. Please check your internet connection.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <main
        className="min-h-screen flex items-center justify-center px-6 py-32"
        style={{ background: "#0A0908" }}
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-lg"
        >
          <div className="mb-10">
            <p
              className="text-xs tracking-[0.3em] uppercase mb-3"
              style={{ color: "rgba(201,168,76,0.7)", fontFamily: "'Montserrat', sans-serif" }}
            >
              Get in Touch
            </p>
            <h1
              className="text-4xl md:text-5xl font-light leading-tight mb-4"
              style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}
            >
              Talk to the{" "}
              <span style={{ color: "#C9A84C", fontStyle: "italic" }}>team</span>
            </h1>
            <p
              className="text-sm leading-relaxed"
              style={{ color: "rgba(245,240,232,0.45)", fontFamily: "'Montserrat', sans-serif" }}
            >
              Questions about Instagram DM automation, your account, pricing, or anything else — send us a message and we'll get back to you directly. No support bots, no ticket queues.
            </p>
            <p
              className="text-xs leading-relaxed mt-4"
              style={{ color: "rgba(245,240,232,0.35)", fontFamily: "'Montserrat', sans-serif" }}
            >
              Prefer email? <a href="mailto:support@aureliusco.in" style={{ color: "rgba(201,168,76,0.8)" }}>support@aureliusco.in</a> · Technical/developer queries: <a href="mailto:developer@aureliusco.in" style={{ color: "rgba(201,168,76,0.8)" }}>developer@aureliusco.in</a> (Harsh Raithatha)
            </p>
          </div>

          {submitted ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="py-16 text-center"
              style={{ border: "1px solid rgba(201,168,76,0.2)", background: "rgba(201,168,76,0.04)" }}
            >
              <div
                className="text-5xl mb-4"
                style={{ color: "#C9A84C", fontFamily: "'Cormorant', serif" }}
              >
                ✦
              </div>
              <h2
                className="text-2xl font-light mb-2"
                style={{ color: "#F5F0E8", fontFamily: "'Cormorant', serif" }}
              >
                Message Received
              </h2>
              <p
                className="text-xs tracking-widest"
                style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}
              >
                We'll be in touch soon.
              </p>
              <button
                onClick={() => { setSubmitted(false); setForm({ name: "", email: "", message: "" }); setTurnstileToken(null); }}
                className="mt-8 text-xs tracking-widest uppercase transition-colors duration-200 cursor-pointer"
                style={{ color: "rgba(201,168,76,0.6)", fontFamily: "'Montserrat', sans-serif" }}
              >
                Send Another
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              {error && (
                <div
                  className="px-4 py-3 text-xs mb-2"
                  style={{ background: "rgba(220, 38, 38, 0.1)", border: "1px solid rgba(220, 38, 38, 0.2)", color: "#ef4444", fontFamily: "'Montserrat', sans-serif" }}
                >
                  {error}
                </div>
              )}

              <div>
                <label
                  htmlFor="name"
                  className="block text-xs tracking-widest uppercase mb-2"
                  style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}
                >
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Your name"
                  className="w-full px-4 py-3 text-sm outline-none transition-all duration-200 bg-[#F5F0E8]/5 border border-[#C9A84C]/30 text-[#F5F0E8] placeholder:text-[#F5F0E8]/30"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-xs tracking-widest uppercase mb-2"
                  style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}
                >
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="w-full px-4 py-3 text-sm outline-none transition-all duration-200 bg-[#F5F0E8]/5 border border-[#C9A84C]/30 text-[#F5F0E8] placeholder:text-[#F5F0E8]/30"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-xs tracking-widest uppercase mb-2"
                  style={{ color: "rgba(245,240,232,0.4)", fontFamily: "'Montserrat', sans-serif" }}
                >
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Tell us what's on your mind..."
                  className="w-full px-4 py-3 text-sm outline-none transition-all duration-200 resize-none bg-[#F5F0E8]/5 border border-[#C9A84C]/30 text-[#F5F0E8] placeholder:text-[#F5F0E8]/30"
                />
              </div>

              <div className="mb-5">
                <Turnstile
                  siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken(null)}
                  options={{ theme: "dark", size: "normal" }}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 text-xs font-medium tracking-widest uppercase transition-all duration-300 cursor-pointer mt-2 disabled:opacity-50"
                style={{ background: "linear-gradient(135deg, #C9A84C, #E8D5A3)", color: "#0A0908", fontFamily: "'Montserrat', sans-serif" }}
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}

          <div className="mt-8 text-center">
            <Link
              href="/"
              className="text-xs tracking-widest uppercase transition-colors duration-200"
              style={{ color: "rgba(245,240,232,0.25)", fontFamily: "'Montserrat', sans-serif" }}
            >
              ← Back to Home
            </Link>
          </div>
        </motion.div>
      </main>
      <Footer />
    </>
  );
}
